import React, { useEffect, useMemo, useState } from "react";
import { FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiCopy, FiSave } from "react-icons/fi";
import AdminEmailTemplatesPanel from "../AdminEmailTemplatesPanel";
import { SETTINGS_NAV_GROUPS, SETTINGS_SECTION_INTROS } from "../adminConstants";
import { formatCurrency, formatTimestamp, generatePromoCode, parseAmount } from "../adminUtils";
import PhoneInput from "../../../components/PhoneInput";
import {
  exportAdminBookings,
  exportAdminPayments,
  exportAdminPartners,
} from "../../../api/adminExports";

const EMPTY_SETTINGS_SECTION = Object.freeze({});

const normalizeGuid = (value) =>
  typeof value === "string" ? value.toLowerCase() : value;

const AdminSettingsTab = ({
  isActive,
  settingsData,
  settingsLoading,
  settingsError,
  settingsSavingSection,
  settingsSaveState,
  settingsActiveSection,
  setSettingsActiveSection,
  settingsHighlightId,
  settingsDirtySections,
  updateSettingsValue,
  handleSettingsSave,
  handleSettingsCancel,
  handleSettingsTestAction,
  handleSettingsResetAction,
  showFeedback,
  refreshAudit,
  rewardVouchers,
  rewardVouchersLoading,
  rewardVouchersError,
  rewardVoucherActionId,
  updateRewardVoucherStatus,
  equipmentSnapshot,
}) => {
  const settingsSectionLookup = useMemo(() => {
    const map = new Map();
    SETTINGS_NAV_GROUPS.forEach((group) => {
      group.sections.forEach((section) => {
        map.set(section.id, section);
      });
    });
    return map;
  }, []);
  const settingsActiveMeta =
    settingsSectionLookup.get(settingsActiveSection) ||
    SETTINGS_NAV_GROUPS[0].sections[0];
  const settingsIntro =
    SETTINGS_SECTION_INTROS[settingsActiveSection] ||
    SETTINGS_SECTION_INTROS[SETTINGS_NAV_GROUPS[0].sections[0].id];
  const settings = settingsData || EMPTY_SETTINGS_SECTION;
  const businessSettings = settings.business || EMPTY_SETTINGS_SECTION;
  const bookingSettings = settings.booking || EMPTY_SETTINGS_SECTION;
  const pricingSettings = settings.pricing || EMPTY_SETTINGS_SECTION;
  const locationsSettings = settings.locations || EMPTY_SETTINGS_SECTION;
  const paymentsSettings = settings.payments || EMPTY_SETTINGS_SECTION;
  const businessCurrency = businessSettings.currencyLocale?.currency || "EUR";

  const hasNumericInput = (value) => /\d/.test(`${value ?? ""}`);

  const formatPercentLabel = (value) => {
    const text = `${value ?? ""}`.trim();
    if (!text) return "";
    if (!hasNumericInput(text)) return "";
    return text.includes("%") ? text : `${text}%`;
  };

  const formatManualPromoDiscount = (promo) => {
    const percentLabel = formatPercentLabel(promo?.discountPercent);
    if (percentLabel) return percentLabel;

    const amountText = `${promo?.discountAmount ?? ""}`.trim();
    if (!amountText) return "";

    const amount = parseAmount(amountText);
    if (!Number.isFinite(amount) || amount <= 0) return "";

    return formatCurrency(amount, businessCurrency);
  };
  const notificationsSettings = settings.notifications || EMPTY_SETTINGS_SECTION;
  const brandingSettings = settings.branding || EMPTY_SETTINGS_SECTION;
  const securitySettings = settings.security || EMPTY_SETTINGS_SECTION;
  const integrationsSettings = settings.integrations || EMPTY_SETTINGS_SECTION;
  const auditSettings = settings.audit || EMPTY_SETTINGS_SECTION;
  const [dangerConfirmText, setDangerConfirmText] = useState("");
  const [promoFilter, setPromoFilter] = useState("manual");
  const [selectedPromoKey, setSelectedPromoKey] = useState(null);
  const bookingsEnabled = bookingSettings.availability?.bookingsEnabled !== false;
  const paymentsEnabled = paymentsSettings.availability?.paymentsEnabled !== false;
  const isDangerConfirmed = dangerConfirmText.trim().toUpperCase() === "CONFIRM";
  const auditSectionDirty = Boolean(settingsDirtySections?.audit);
  const auditSeasonDirty =
    settingsActiveSection === "audit" && Boolean(settingsDirtySections?.booking);
  const isSavingActiveSection =
    settingsSavingSection === settingsActiveSection ||
    (settingsActiveSection === "audit" && settingsSavingSection === "booking");
  const isSectionDisabled = settingsLoading || isSavingActiveSection;
  const saveState =
    settingsSaveState?.[settingsActiveSection] ||
    (settingsActiveSection === "audit" ? settingsSaveState?.booking : null);
  const isSectionDirty =
    settingsActiveSection === "audit"
      ? auditSectionDirty || auditSeasonDirty
      : Boolean(settingsDirtySections?.[settingsActiveSection]);
  const saveStatus = (() => {
    if (settingsLoading || !saveState?.status) return null;
    if (saveState.status === "saved") {
      return { label: "Saved", variant: "saved" };
    }
    if (saveState.status === "error") {
      return { label: saveState.message || "Save failed.", variant: "error" };
    }
    if (saveState.status === "saving") {
      return { label: "Saving...", variant: "saving" };
    }
    return null;
  })();
  const handleActiveSectionSave = async () => {
    if (settingsActiveSection === "audit") {
      if (auditSectionDirty) {
        await handleSettingsSave("audit");
      }
      if (auditSeasonDirty) {
        await handleSettingsSave("booking");
      }
      return;
    }

    await handleSettingsSave(settingsActiveSection);
  };
  const handleActiveSectionCancel = () => {
    if (settingsActiveSection === "audit") {
      if (auditSectionDirty) {
        handleSettingsCancel("audit");
      }
      if (auditSeasonDirty) {
        handleSettingsCancel("booking");
      }
      return;
    }

    handleSettingsCancel(settingsActiveSection);
  };
  const handleValueChange = (sectionId, path) => (event) => {
    updateSettingsValue(sectionId, path, event.target.value);
  };

  const handlePhoneValueChange = (sectionId, path) => (value) => {
    updateSettingsValue(sectionId, path, value);
  };

  const handleDiscountPercentChange = (event) => {
    const nextValue = event.target.value;
    if (
      hasNumericInput(nextValue) &&
      hasNumericInput(pricingSettings.discounts?.discountAmount)
    ) {
      updateSettingsValue("pricing", ["discounts", "discountAmount"], "");
    }
    updateSettingsValue("pricing", ["discounts", "discount"], nextValue);
  };

  const handleDiscountAmountChange = (event) => {
    const nextValue = event.target.value;
    if (
      hasNumericInput(nextValue) &&
      hasNumericInput(pricingSettings.discounts?.discount)
    ) {
      updateSettingsValue("pricing", ["discounts", "discount"], "");
    }
    updateSettingsValue("pricing", ["discounts", "discountAmount"], nextValue);
  };
  const handleCheckboxChange = (sectionId, path) => (event) => {
    updateSettingsValue(sectionId, path, event.target.checked);
  };
  const handleGeneratePromoCode = () => {
    const discounts = pricingSettings.discounts || {};
    const createdUtc = new Date().toISOString();
    const code = generatePromoCode({
      discount: discounts.discount,
      discountAmount: discounts.discountAmount,
      usageLimit: discounts.usageLimit,
      validity: discounts.validity,
    });
    const discountPercent = hasNumericInput(discounts.discount)
      ? discounts.discount || ""
      : "";
    const discountAmount =
      !discountPercent && hasNumericInput(discounts.discountAmount)
        ? discounts.discountAmount || ""
        : "";
    const newPromo = {
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      code,
      discountPercent,
      discountAmount,
      validity: discounts.validity || "",
      usageLimit: discounts.usageLimit || "",
      usageCount: 0,
      createdUtc,
      cancelledUtc: null,
    };
    const nextList = [...manualPromoCodes, newPromo];
    setManualPromoCodes(nextList);
    updateSettingsValue("pricing", ["discounts", "codeName"], code);
    setPromoFilter("manual");
    setSelectedPromoKey(newPromo.id);
  };
  const handleCopyPromoCode = async (event, code) => {
    event?.stopPropagation?.();
    const trimmed = String(code || "").trim();
    if (!trimmed) return;
    if (typeof window === "undefined") return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(trimmed);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = trimmed;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      showFeedback("Voucher code copied.", "success");
    } catch {
      showFeedback("Unable to copy voucher code.", "warning");
    }
  };
  const handleCopyPromoKeyDown = (event, code) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCopyPromoCode(event, code);
    }
  };
  const parseValidityRange = (value) => {
    const matches = `${value ?? ""}`.match(/\d{4}-\d{2}-\d{2}/g);
    return {
      start: matches?.[0] ?? "",
      end: matches?.[1] ?? "",
    };
  };
  const buildValidityRange = (start, end) => {
    if (!start && !end) return "";
    if (start && end) return `${start} to ${end}`;
    if (start) return `${start} to`;
    return `to ${end}`;
  };
  const { start: validityStart, end: validityEnd } = useMemo(
    () => parseValidityRange(pricingSettings.discounts?.validity),
    [pricingSettings.discounts?.validity]
  );
  const updateDiscountValidity = (nextStart, nextEnd) => {
    updateSettingsValue(
      "pricing",
      ["discounts", "validity"],
      buildValidityRange(nextStart, nextEnd)
    );
  };
  const manualPromoCodes = Array.isArray(pricingSettings.promoCodes)
    ? pricingSettings.promoCodes
    : [];
  const rewardPromoCodes = Array.isArray(rewardVouchers) ? rewardVouchers : [];
  const getPromoKey = (promo) => promo?.id || promo?.code || "";
  const normalizedManualPromoCodes = manualPromoCodes.map((promo) => ({
    ...promo,
    id: promo.id ?? promo.code,
    type: "manual",
  }));
  const normalizedRewardPromoCodes = rewardPromoCodes.map((promo) => ({
    ...promo,
    type: "reward",
  }));
  const promoList =
    promoFilter === "reward" ? normalizedRewardPromoCodes : normalizedManualPromoCodes;
  const selectedPromo =
    promoList.find((promo) => getPromoKey(promo) === selectedPromoKey) || null;
  useEffect(() => {
    if (promoList.length === 0) {
      if (selectedPromoKey !== null) setSelectedPromoKey(null);
      return;
    }
    const exists = promoList.some((promo) => getPromoKey(promo) === selectedPromoKey);
    if (!exists) {
      setSelectedPromoKey(getPromoKey(promoList[0]));
    }
  }, [promoList, selectedPromoKey]);
  const getPromoStatus = (promo) => {
    if (!promo) return "unknown";
    if (promo.redeemedUtc) return "redeemed";
    if (promo.cancelledUtc) return "cancelled";
    if (promo.type === "manual") {
      const usageCount = parseOptionalInt(promo.usageCount) ?? 0;
      const usageLimit = parseOptionalInt(promo.usageLimit);
      if (typeof usageLimit === "number" && usageLimit > 0 && usageCount >= usageLimit) {
        return "redeemed";
      }
    }
    return "active";
  };
  const getPromoStatusLabel = (status) => {
    if (status === "redeemed") return "Redeemed";
    if (status === "cancelled") return "Cancelled";
    if (status === "active") return "Active";
    return "Unknown";
  };
  const parseOptionalInt = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const buildSectionUpdate = (section, path, value) => {
    const next = section ? JSON.parse(JSON.stringify(section)) : {};
    let cursor = next;
    for (let i = 0; i < path.length - 1; i += 1) {
      const key = path[i];
      if (!cursor[key] || typeof cursor[key] !== "object") {
        cursor[key] = typeof path[i + 1] === "number" ? [] : {};
      }
      cursor = cursor[key];
    }
    cursor[path[path.length - 1]] = value;
    return next;
  };
  const rowsById = useMemo(() => {
    const rows = Array.isArray(pricingSettings?.basePricing?.rows)
      ? pricingSettings.basePricing.rows
      : [];
    return rows.reduce((acc, row) => {
      const id = row?.id ?? row?.equipmentTypeId;
      if (id) {
        acc[normalizeGuid(id)] = row;
      }
      return acc;
    }, {});
  }, [pricingSettings]);
  const rowsByName = useMemo(() => {
    const rows = Array.isArray(pricingSettings?.basePricing?.rows)
      ? pricingSettings.basePricing.rows
      : [];
    return rows.reduce((acc, row) => {
      const name = typeof row?.name === "string" ? row.name.trim().toLowerCase() : "";
      if (name) {
        acc[name] = row;
      }
      return acc;
    }, {});
  }, [pricingSettings]);
  const basePricingRows = useMemo(
    () => {
      const pricingRows = Array.isArray(pricingSettings?.basePricing?.rows)
        ? pricingSettings.basePricing.rows
        : [];
      const equipmentRows = (Array.isArray(equipmentSnapshot) ? equipmentSnapshot : []).map(
        (equipment) => {
          const id = normalizeGuid(equipment.id ?? equipment.equipmentTypeId);
          const name = equipment.name ?? equipment.equipmentTypeName ?? "Equipment";
          return (
            rowsById[id] ||
            rowsByName[String(name).trim().toLowerCase()] || {
              id: equipment.id ?? equipment.equipmentTypeId,
              name,
              firstHour: "",
              nextHalfHour: "",
              fullDay: "",
              weekly: "",
              trip: "",
            }
          );
        }
      );

      if (equipmentRows.length === 0) {
        return pricingRows;
      }

      const usedKeys = new Set(
        equipmentRows.map((row) => {
          const id = row?.id ?? row?.equipmentTypeId;
          if (id) return `id:${normalizeGuid(id)}`;
          const name = typeof row?.name === "string" ? row.name.trim().toLowerCase() : "";
          return `name:${name}`;
        })
      );

      const extraRows = pricingRows.filter((row) => {
        const id = row?.id ?? row?.equipmentTypeId;
        if (id) {
          return !usedKeys.has(`id:${normalizeGuid(id)}`);
        }
        const name = typeof row?.name === "string" ? row.name.trim().toLowerCase() : "";
        return !usedKeys.has(`name:${name}`);
      });

      return [...equipmentRows, ...extraRows];
    },
    [equipmentSnapshot, pricingSettings, rowsById, rowsByName]
  );
  const downloadBlob = (blob, fileName, fallback) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      fileName ||
      fallback ||
      `wayli-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const setManualPromoCodes = (nextList) => {
    updateSettingsValue("pricing", ["promoCodes"], nextList);
  };
  const handleDeleteManualPromo = (promoKey) => {
    if (!promoKey) return;
    const promo = manualPromoCodes.find((entry) => getPromoKey(entry) === promoKey);
    if (!promo) return;
    const codeLabel = String(promo.code || "").trim();
    const shouldDelete =
      typeof window === "undefined"
        ? true
        : window.confirm(
            `Delete promo code ${codeLabel ? `"${codeLabel}"` : ""}? This cannot be undone.`
          );
    if (!shouldDelete) return;

    const nextList = manualPromoCodes.filter((entry) => getPromoKey(entry) !== promoKey);
    setManualPromoCodes(nextList);
    showFeedback?.("Promo code deleted.", "success");
  };
  const updateManualPromo = (promoKey, updates) => {
    const index = manualPromoCodes.findIndex(
      (promo) => getPromoKey(promo) === promoKey
    );
    if (index < 0) return;
    const nextList = manualPromoCodes.map((promo, idx) =>
      idx === index ? { ...promo, ...updates } : promo
    );
    setManualPromoCodes(nextList);
  };
  const handleManualPromoStatusChange = (promoKey, isActive) => {
    updateManualPromo(promoKey, {
      cancelledUtc: isActive ? null : new Date().toISOString(),
    });
  };
  const selectedPromoStatus = getPromoStatus(selectedPromo);
  const selectedPromoStatusLabel = getPromoStatusLabel(selectedPromoStatus);
  const isSelectedRewardPromo = selectedPromo?.type === "reward";
  const isSelectedPromoRedeemed = selectedPromoStatus === "redeemed";
  const isSelectedPromoCancelled = selectedPromoStatus === "cancelled";
  const isPromoActionLoading =
    isSelectedRewardPromo && selectedPromo?.id === rewardVoucherActionId;
  const promoUsageCount = isSelectedRewardPromo
    ? selectedPromo?.redeemedUtc
      ? 1
      : 0
    : parseOptionalInt(selectedPromo?.usageCount) ?? 0;
  const promoUsageLimit = isSelectedRewardPromo
    ? 1
    : parseOptionalInt(selectedPromo?.usageLimit);
  const isPromoUsageLimitReached =
    typeof promoUsageLimit === "number" && promoUsageCount >= promoUsageLimit;
  const systemHealthServices = useMemo(() => {
    const base = Array.isArray(auditSettings.systemHealth?.services)
      ? auditSettings.systemHealth.services
      : [];
    const normalized = base.map((service) => {
      if (service?.name === "Booking engine") {
        return {
          ...service,
          status: bookingsEnabled ? "Enabled" : "Disabled by admin",
        };
      }
      if (service?.name === "Payments") {
        return {
          ...service,
          status: paymentsEnabled ? "Enabled" : "Disabled by admin",
        };
      }
      return service;
    });
    const hasBooking = normalized.some((service) => service?.name === "Booking engine");
    const hasPayments = normalized.some((service) => service?.name === "Payments");
    const withBooking = hasBooking
      ? normalized
      : [
          {
            name: "Booking engine",
            status: bookingsEnabled ? "Enabled" : "Disabled by admin",
          },
          ...normalized,
        ];
    if (hasPayments) return withBooking;
    return [
      {
        name: "Payments",
        status: paymentsEnabled ? "Enabled" : "Disabled by admin",
      },
      ...withBooking,
    ];
  }, [auditSettings.systemHealth?.services, bookingsEnabled, paymentsEnabled]);
  const handleExportBookings = async () => {
    try {
      const { blob, fileName } = await exportAdminBookings();
      downloadBlob(blob, fileName, "wayli-bookings.csv");
      showFeedback("Bookings export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export bookings.", "warning");
    }
  };
  const handleExportPayments = async () => {
    try {
      const { blob, fileName } = await exportAdminPayments();
      downloadBlob(blob, fileName, "wayli-payments.csv");
      showFeedback("Payments export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export payments.", "warning");
    }
  };
  const handleExportPartners = async () => {
    try {
      const { blob, fileName } = await exportAdminPartners();
      downloadBlob(blob, fileName, "wayli-partners.csv");
      showFeedback("Partners export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export partners.", "warning");
    }
  };
  const handleBookingGateToggle = async () => {
    if (isSectionDisabled) return;
    if (!isDangerConfirmed) {
      showFeedback("Type CONFIRM to continue.", "warning");
      return;
    }

    const nextEnabled = !(bookingsEnabled && paymentsEnabled);
    const nextBookingSection = buildSectionUpdate(
      settingsData?.booking,
      ["availability", "bookingsEnabled"],
      nextEnabled
    );
    const nextPaymentsSection = buildSectionUpdate(
      settingsData?.payments,
      ["availability", "paymentsEnabled"],
      nextEnabled
    );

    updateSettingsValue("booking", ["availability", "bookingsEnabled"], nextEnabled);
    updateSettingsValue("payments", ["availability", "paymentsEnabled"], nextEnabled);

    await handleSettingsSave("booking", nextBookingSection);
    await handleSettingsSave("payments", nextPaymentsSection);

    setDangerConfirmText("");
    showFeedback(
      nextEnabled ? "Bookings and payments enabled." : "Bookings and payments disabled.",
      nextEnabled ? "success" : "warning"
    );
  };
  const getSettingsCardClass = (cardId) =>
    `admin-settings__card ${
      settingsHighlightId === cardId ? "admin-settings__card--highlight" : ""
    }`.trim();

  return (
<div
              className={`admin-tab ${
                isActive ? "admin-tab--active" : ""
              }`.trim()}
            >
              <section className="admin-panel admin-settings">
                <div className="admin-settings__layout">
                  <aside className="admin-settings__sidebar">
                    {SETTINGS_NAV_GROUPS.map((group) => (
                      <div key={group.id} className="admin-settings__nav-group">
                        <span className="admin-settings__nav-title">{group.label}</span>
                        {group.sections.map((section) => {
                          const Icon = section.icon;
                          const isPotential = Boolean(section.potential);
                          return (
                            <button
                              key={section.id}
                              type="button"
                              className={`admin-settings__nav-item ${
                                settingsActiveSection === section.id
                                  ? "admin-settings__nav-item--active"
                                  : ""
                              } ${section.danger ? "admin-settings__nav-item--danger" : ""} ${
                                isPotential ? "admin-settings__nav-item--potential" : ""
                              }`.trim()}
                              onClick={() => setSettingsActiveSection(section.id)}
                              disabled={isPotential}
                            >
                              <Icon aria-hidden="true" />
                              <span className="admin-settings__nav-label">
                                <span>{section.label}</span>
                                {isPotential && (
                                  <span className="admin-settings__nav-tag">Potentially needed</span>
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </aside>
                  <div className="admin-settings__content">
                    <div className="admin-settings__section">
                      <header className="admin-settings__section-header">
                        <div className="admin-settings__section-intro">
                          <p className="admin-section__eyebrow">{settingsIntro.eyebrow}</p>
                          <div className="admin-settings__section-title-row">
                            <h2>{settingsActiveMeta.label}</h2>
                            <div className="admin-settings__section-actions">
                              {settingsActiveMeta.danger && (
                                <span className="admin-settings__section-warning">
                                  <FiAlertTriangle aria-hidden="true" />
                                  Sensitive area
                                </span>
                              )}
                          {saveStatus && (
                            <span
                              className={`admin-settings__save-state admin-settings__save-state--${saveStatus.variant}`}
                            >
                              {saveStatus.label}
                                </span>
                              )}
                              <button
                                type="button"
                                className="admin-button admin-button--primary"
                                onClick={handleActiveSectionSave}
                                disabled={!isSectionDirty || isSectionDisabled}
                              >
                                <FiSave aria-hidden="true" />
                                {isSavingActiveSection ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                className="admin-button admin-button--outline"
                                onClick={handleActiveSectionCancel}
                                disabled={!isSectionDirty || isSectionDisabled}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                          <p>{settingsIntro.description}</p>
                          {settingsError && (
                            <p className="admin-settings__error">{settingsError}</p>
                          )}
                          {settingsLoading && (
                            <p className="admin-settings__status">Loading settings...</p>
                          )}
                        </div>
                      </header>

                      {settingsActiveSection === "business" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-company-profile"
                            className={getSettingsCardClass("company-profile")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Company profile</h3>
                              <p>Used on invoices, exports, and staff notifications.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Business name</label>
                                <input
                                  className="admin-input"
                                  value={businessSettings.companyProfile?.businessName || ""}
                                  placeholder="Business name"
                                  onChange={handleValueChange("business", [
                                    "companyProfile",
                                    "businessName",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Legal entity type</label>
                                <select
                                  className="admin-select"
                                  value={businessSettings.companyProfile?.legalEntityType || ""}
                                  onChange={handleValueChange("business", [
                                    "companyProfile",
                                    "legalEntityType",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option>GmbH</option>
                                  <option>OG</option>
                                  <option>Einzelunternehmen</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Contact email</label>
                                <input
                                  className="admin-input"
                                  value={businessSettings.companyProfile?.contactEmail || ""}
                                  onChange={handleValueChange("business", [
                                    "companyProfile",
                                    "contactEmail",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Contact phone</label>
                                <PhoneInput
                                  id="settings-contact-phone"
                                  name="contactPhone"
                                  value={businessSettings.companyProfile?.contactPhone || ""}
                                  onChange={handlePhoneValueChange("business", [
                                    "companyProfile",
                                    "contactPhone",
                                  ])}
                                  autoComplete="tel"
                                  disabled={isSectionDisabled}
                                  placeholder="Phone number"
                                  className="admin-phone"
                                  selectClassName="admin-select admin-select--compact"
                                  inputClassName="admin-input"
                                  countryLabel="Country code"
                                  numberLabel="Contact phone"
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Business hours default</label>
                                <input
                                  className="admin-input"
                                  value={businessSettings.companyProfile?.hoursDefault || ""}
                                  placeholder="08:00–20:00"
                                  onChange={handleValueChange("business", [
                                    "companyProfile",
                                    "hoursDefault",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-tax-vat"
                            className={getSettingsCardClass("tax-vat")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Tax / VAT defaults</h3>
                              <p>Applied to invoices and exports.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(businessSettings.taxVat?.vatEnabled)}
                                  onChange={handleCheckboxChange("business", [
                                    "taxVat",
                                    "vatEnabled",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                                VAT enabled
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label" htmlFor="vatRateSetting">
                                  VAT rate (%)
                                </label>
                                <input
                                  id="vatRateSetting"
                                  className="admin-input"
                                  inputMode="decimal"
                                  value={businessSettings.taxVat?.vatRatePercent || ""}
                                  onChange={handleValueChange("business", [
                                    "taxVat",
                                    "vatRatePercent",
                                  ])}
                                  disabled={isSectionDisabled}
                                  placeholder="20"
                                />
                              </div>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(businessSettings.taxVat?.vatIncluded)}
                                  onChange={handleCheckboxChange("business", [
                                    "taxVat",
                                    "vatIncluded",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                                Prices include VAT
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Default tax category</label>
                                <select
                                  className="admin-select"
                                  value={businessSettings.taxVat?.taxCategory || "standard"}
                                  onChange={handleValueChange("business", [
                                    "taxVat",
                                    "taxCategory",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="standard">Standard (20%)</option>
                                  <option value="reduced">Reduced</option>
                                  <option value="exempt">Exempt</option>
                                </select>
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-currency-locale"
                            className={getSettingsCardClass("currency-locale")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Currency & locale</h3>
                              <p>Default formatting for bookings and receipts.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Currency</label>
                                <select
                                  className="admin-select"
                                  value={businessSettings.currencyLocale?.currency || ""}
                                  onChange={handleValueChange("business", [
                                    "currencyLocale",
                                    "currency",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option>EUR</option>
                                  <option>USD</option>
                                  <option>GBP</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Language default</label>
                                <select
                                  className="admin-select"
                                  value={businessSettings.currencyLocale?.language || ""}
                                  onChange={handleValueChange("business", [
                                    "currencyLocale",
                                    "language",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option>EN</option>
                                  <option>DE</option>
                                  <option>UA</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Timezone</label>
                                <select
                                  className="admin-select"
                                  value={businessSettings.currencyLocale?.timezone || ""}
                                  onChange={handleValueChange("business", [
                                    "currencyLocale",
                                    "timezone",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option>Europe/Vienna</option>
                                  <option>Europe/Berlin</option>
                                  <option>Europe/Prague</option>
                                </select>
                              </div>
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "booking" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-booking-window"
                            className={getSettingsCardClass("booking-window")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Booking window</h3>
                              <p>Define how far in advance bookings can be made.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Min notice (minutes)</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.window?.minNoticeMinutes || ""}
                                  onChange={handleValueChange("booking", [
                                    "window",
                                    "minNoticeMinutes",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Max advance (days)</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.window?.maxAdvanceDays || ""}
                                  onChange={handleValueChange("booking", [
                                    "window",
                                    "maxAdvanceDays",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">
                                  Auto-expire unpaid (minutes)
                                </label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.window?.autoExpireMinutes || ""}
                                  onChange={handleValueChange("booking", [
                                    "window",
                                    "autoExpireMinutes",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-capacity-handling"
                            className={getSettingsCardClass("capacity-handling")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Capacity handling</h3>
                              <p>Prevent overbooking and lost inventory.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(bookingSettings.capacityHandling?.allowOverbooking)}
                                  onChange={handleCheckboxChange("booking", [
                                    "capacityHandling",
                                    "allowOverbooking",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Overbooking allowed
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(bookingSettings.capacityHandling?.holdInventory)}
                                  onChange={handleCheckboxChange("booking", [
                                    "capacityHandling",
                                    "holdInventory",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Hold inventory while payment pending
                              </label>
                            </div>
                          </article>

                          <article
                            id="settings-card-cancellation-policy"
                            className={getSettingsCardClass("cancellation-policy")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Cancellation policy</h3>
                              <p>Define fees and no-show rules.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Free cancellation (hours)</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.cancellationPolicy?.freeCancellationHours || ""}
                                  onChange={handleValueChange("booking", [
                                    "cancellationPolicy",
                                    "freeCancellationHours",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Late cancellation fee</label>
                                <input
                                  className="admin-input"
                                  value={bookingSettings.cancellationPolicy?.lateCancellationFee || ""}
                                  onChange={handleValueChange("booking", [
                                    "cancellationPolicy",
                                    "lateCancellationFee",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">No-show policy</label>
                                <input
                                  className="admin-input"
                                  value={bookingSettings.cancellationPolicy?.noShowPolicy || ""}
                                  onChange={handleValueChange("booking", [
                                    "cancellationPolicy",
                                    "noShowPolicy",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-late-checkin-policy"
                            className={getSettingsCardClass("late-checkin-policy")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Late check-in policy</h3>
                              <p>Charge for overdue check-ins after a grace window.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Grace period (minutes)</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.lateCheckinPolicy?.graceMinutes || ""}
                                  onChange={handleValueChange("booking", [
                                    "lateCheckinPolicy",
                                    "graceMinutes",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Fee interval (minutes)</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.lateCheckinPolicy?.intervalMinutes || ""}
                                  onChange={handleValueChange("booking", [
                                    "lateCheckinPolicy",
                                    "intervalMinutes",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Fee per interval (EUR)</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  value={bookingSettings.lateCheckinPolicy?.feePerInterval || ""}
                                  onChange={handleValueChange("booking", [
                                    "lateCheckinPolicy",
                                    "feePerInterval",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-qr-rules"
                            className={getSettingsCardClass("qr-rules")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>QR-based pickup/return</h3>
                              <p>Control scan requirements at pickup and return.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(bookingSettings.qrRules?.pickupRequiresScan)}
                                  onChange={handleCheckboxChange("booking", [
                                    "qrRules",
                                    "pickupRequiresScan",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Pickup confirmation requires scan
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(bookingSettings.qrRules?.returnRequiresScan)}
                                  onChange={handleCheckboxChange("booking", [
                                    "qrRules",
                                    "returnRequiresScan",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Return confirmation requires scan
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(bookingSettings.qrRules?.allowManualOverride)}
                                  onChange={handleCheckboxChange("booking", [
                                    "qrRules",
                                    "allowManualOverride",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Allow manual override (owner only)
                              </label>
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "pricing" && (
                        <div className="admin-settings__section-grid admin-settings__section-grid--pricing-layout">
                          <article
                            id="settings-card-base-pricing"
                            className={getSettingsCardClass("base-pricing")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Base pricing</h3>
                              <p>Set equipment pricing by duration.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__price-table">
                              <div className="admin-settings__price-row admin-settings__price-row--head">
                                <span>Equipment</span>
                                <span>Legacy hour</span>
                                <span>Legacy 30 min</span>
                                <span>Daily</span>
                                <span>Weekly</span>
                                <span>Legacy trip</span>
                              </div>
                              {basePricingRows.map((row, index) => (
                                <div
                                  key={row.id ?? row.name ?? index}
                                  className="admin-settings__price-row"
                                >
                                  <span>{row.name || "Equipment"}</span>
                                  <input
                                    className="admin-input"
                                    value={row.firstHour || ""}
                                    onChange={handleValueChange("pricing", [
                                      "basePricing",
                                      "rows",
                                      index,
                                      "firstHour",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                  <input
                                    className="admin-input"
                                    value={row.nextHalfHour || ""}
                                    onChange={handleValueChange("pricing", [
                                      "basePricing",
                                      "rows",
                                      index,
                                      "nextHalfHour",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                  <input
                                    className="admin-input"
                                    value={row.fullDay || ""}
                                    onChange={handleValueChange("pricing", [
                                      "basePricing",
                                      "rows",
                                      index,
                                      "fullDay",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                  <input
                                    className="admin-input"
                                    value={row.weekly || ""}
                                    onChange={handleValueChange("pricing", [
                                      "basePricing",
                                      "rows",
                                      index,
                                      "weekly",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                  <input
                                    className="admin-input"
                                    value={row.trip || ""}
                                    onChange={handleValueChange("pricing", [
                                      "basePricing",
                                      "rows",
                                      index,
                                      "trip",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                </div>
                              ))}
                            </div>
                          </article>

                          <article
                            id="settings-card-discounts"
                            className={getSettingsCardClass("discounts")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Discounts & promo codes</h3>
                              <p>Manage promos without touching code.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__discounts-layout">
                              <div className="admin-settings__discounts-form">
                                <div className="admin-settings__form-grid">
                                  <div className="admin-form__row">
                                    <label className="admin-form__label">Code name</label>
                                    <div className="admin-form__inline">
                                      <input
                                        className="admin-input"
                                        value={pricingSettings.discounts?.codeName || ""}
                                        onChange={handleValueChange("pricing", [
                                          "discounts",
                                          "codeName",
                                        ])}
                                        disabled={isSectionDisabled}
                                      />
                                      <button
                                        type="button"
                                        className="admin-button admin-button--ghost"
                                        onClick={handleGeneratePromoCode}
                                        disabled={isSectionDisabled}
                                      >
                                        Generate
                                      </button>
                                    </div>
                                  </div>
                                  <div className="admin-form__row">
                                    <label className="admin-form__label">
                                      Discount percent / amount
                                    </label>
                                    <div className="admin-form__inline admin-form__inline--nowrap">
                                      <input
                                        className="admin-input"
                                        value={pricingSettings.discounts?.discount || ""}
                                        onChange={handleDiscountPercentChange}
                                        placeholder="%"
                                        aria-label="Discount percent"
                                        disabled={isSectionDisabled}
                                      />
                                      <input
                                        className="admin-input"
                                        type="number"
                                        value={pricingSettings.discounts?.discountAmount || ""}
                                        onChange={handleDiscountAmountChange}
                                        placeholder="€"
                                        aria-label="Discount amount"
                                        min="0"
                                        step="0.01"
                                        disabled={isSectionDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="admin-form__row">
                                    <label className="admin-form__label">
                                      Validity window
                                    </label>
                                    <div className="admin-form__inline">
                                      <input
                                        className="admin-input"
                                        type="date"
                                        value={validityStart}
                                        onChange={(event) =>
                                          updateDiscountValidity(
                                            event.target.value,
                                            validityEnd
                                          )
                                        }
                                        disabled={isSectionDisabled}
                                      />
                                      <span className="admin-form__divider">to</span>
                                      <input
                                        className="admin-input"
                                        type="date"
                                        value={validityEnd}
                                        onChange={(event) =>
                                          updateDiscountValidity(
                                            validityStart,
                                            event.target.value
                                          )
                                        }
                                        disabled={isSectionDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="admin-form__row">
                                    <label className="admin-form__label">Usage limit</label>
                                    <input
                                      className="admin-input"
                                      type="number"
                                      value={pricingSettings.discounts?.usageLimit || ""}
                                      onChange={handleValueChange("pricing", [
                                        "discounts",
                                        "usageLimit",
                                      ])}
                                      min="0"
                                      disabled={isSectionDisabled}
                                    />
                                  </div>
                                </div>
                                <div className="admin-settings__promo-panel">
                                <div className="admin-settings__promo-panel-head">
                                  <div className="admin-settings__promo-panel-intro">
                                    <h4 className="admin-settings__promo-title">
                                      Promo code library
                                    </h4>
                                    <p className="admin-settings__promo-subtitle">
                                      Review reward vouchers and manual promos.
                                    </p>
                                  </div>
                                  <div className="admin-settings__promo-toggle">
                                    <button
                                      type="button"
                                      className={`admin-settings__promo-toggle-button ${
                                        promoFilter === "reward"
                                          ? "admin-settings__promo-toggle-button--active"
                                          : ""
                                      }`.trim()}
                                      onClick={() => setPromoFilter("reward")}
                                    >
                                      Reward vouchers
                                    </button>
                                    <button
                                      type="button"
                                      className={`admin-settings__promo-toggle-button ${
                                        promoFilter === "manual"
                                          ? "admin-settings__promo-toggle-button--active"
                                          : ""
                                      }`.trim()}
                                      onClick={() => setPromoFilter("manual")}
                                    >
                                      Manual promos
                                    </button>
                                  </div>
                                </div>
                                <div className="admin-settings__promo-body">
                                  <div className="admin-settings__promo-list">
                                    {promoFilter === "reward" ? (
                                      rewardVouchersLoading ? (
                                        <p className="admin-settings__promo-empty">
                                          Loading reward vouchers...
                                        </p>
                                      ) : rewardVouchersError ? (
                                        <p className="admin-settings__promo-empty">
                                          {rewardVouchersError}
                                        </p>
                                      ) : promoList.length === 0 ? (
                                        <p className="admin-settings__promo-empty">
                                          No reward vouchers yet.
                                        </p>
                                      ) : (
                                        promoList.map((promo) => {
                                          const promoKey = getPromoKey(promo);
                                          const status = getPromoStatus(promo);
                                          return (
                                            <button
                                              key={promoKey}
                                              type="button"
                                              className={`admin-settings__promo-item ${
                                                selectedPromoKey === promoKey
                                                  ? "admin-settings__promo-item--active"
                                                  : ""
                                              }`.trim()}
                                              onClick={() => setSelectedPromoKey(promoKey)}
                                            >
                                              <div className="admin-settings__promo-item-main">
                                                <div className="admin-settings__promo-code-row">
                                                  <span className="admin-settings__promo-code">
                                                    {promo.code || "—"}
                                                  </span>
                                                  {promo.code ? (
                                                    <span
                                                      className="admin-settings__promo-copy"
                                                      role="button"
                                                      tabIndex={0}
                                                      aria-label={`Copy ${promo.code}`}
                                                      onClick={(event) =>
                                                        handleCopyPromoCode(event, promo.code)
                                                      }
                                                      onKeyDown={(event) =>
                                                        handleCopyPromoKeyDown(event, promo.code)
                                                      }
                                                    >
                                                      <FiCopy aria-hidden="true" />
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <span className="admin-settings__promo-meta">
                                                  Reward voucher
                                                </span>
                                              </div>
                                              <span
                                                className={`admin-settings__promo-status admin-settings__promo-status--${status}`}
                                              >
                                                {getPromoStatusLabel(status)}
                                              </span>
                                            </button>
                                          );
                                        })
                                      )
                                    ) : promoList.length === 0 ? (
                                      <p className="admin-settings__promo-empty">
                                        No manual promo codes yet.
                                      </p>
                                    ) : (
                                      promoList.map((promo) => {
                                        const promoKey = getPromoKey(promo);
                                        const status = getPromoStatus(promo);
                                        return (
                                          <button
                                            key={promoKey}
                                            type="button"
                                            className={`admin-settings__promo-item ${
                                              selectedPromoKey === promoKey
                                                ? "admin-settings__promo-item--active"
                                                : ""
                                            }`.trim()}
                                            onClick={() => setSelectedPromoKey(promoKey)}
                                          >
                                            <div className="admin-settings__promo-item-main">
                                              <div className="admin-settings__promo-code-row">
                                                <span className="admin-settings__promo-code">
                                                  {promo.code || "—"}
                                                </span>
                                                {promo.code ? (
                                                  <span
                                                    className="admin-settings__promo-copy"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Copy ${promo.code}`}
                                                    onClick={(event) =>
                                                      handleCopyPromoCode(event, promo.code)
                                                    }
                                                    onKeyDown={(event) =>
                                                      handleCopyPromoKeyDown(event, promo.code)
                                                    }
                                                  >
                                                    <FiCopy aria-hidden="true" />
                                                  </span>
                                                ) : null}
                                              </div>
                                              <span className="admin-settings__promo-meta">
                                                {formatManualPromoDiscount(promo) ||
                                                  "Manual discount"}
                                              </span>
                                            </div>
                                            <span
                                              className={`admin-settings__promo-status admin-settings__promo-status--${status}`}
                                            >
                                              {getPromoStatusLabel(status)}
                                            </span>
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                  <div className="admin-settings__promo-detail">
                                    {selectedPromo ? (
                                      <>
                                        <div className="admin-settings__promo-detail-head">
                                          <span className="admin-settings__promo-detail-code">
                                            {selectedPromo.code || "—"}
                                          </span>
                                          <span
                                            className={`admin-settings__promo-status admin-settings__promo-status--${selectedPromoStatus}`}
                                          >
                                            {selectedPromoStatusLabel}
                                          </span>
                                        </div>
                                        <div className="admin-settings__promo-detail-grid">
                                          <div>
                                            <span className="admin-settings__promo-meta-label">
                                              Discount
                                            </span>
                                            <p className="admin-settings__promo-meta-value">
                                              {isSelectedRewardPromo
                                                ? "Reward voucher"
                                                : formatManualPromoDiscount(selectedPromo) ||
                                                  "—"}
                                            </p>
                                          </div>
                                          <div>
                                            <span className="admin-settings__promo-meta-label">
                                              Validity
                                            </span>
                                            <p className="admin-settings__promo-meta-value">
                                              {isSelectedRewardPromo
                                                ? "—"
                                                : selectedPromo.validity || "—"}
                                            </p>
                                          </div>
                                          <div>
                                            <span className="admin-settings__promo-meta-label">
                                              Usage
                                            </span>
                                            <p className="admin-settings__promo-meta-value">
                                              {promoUsageLimit
                                                ? `${promoUsageCount} / ${promoUsageLimit}`
                                                : `${promoUsageCount} / —`}
                                              {isPromoUsageLimitReached && (
                                                <span
                                                  className="admin-settings__promo-usage-check"
                                                  aria-label="Usage limit reached"
                                                >
                                                  ✓
                                                </span>
                                              )}
                                            </p>
                                          </div>
                                          <div>
                                            <span className="admin-settings__promo-meta-label">
                                              Issued
                                            </span>
                                            <p className="admin-settings__promo-meta-value">
                                              {formatTimestamp(
                                                isSelectedRewardPromo
                                                  ? selectedPromo.issuedUtc
                                                  : selectedPromo.createdUtc
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="admin-settings__promo-actions">
                                          <button
                                            type="button"
                                            className="admin-button admin-button--outline"
                                            onClick={() => {
                                              const promoKey = getPromoKey(selectedPromo);
                                              if (isSelectedRewardPromo) {
                                                updateRewardVoucherStatus(
                                                  selectedPromo.id,
                                                  true
                                                );
                                              } else {
                                                handleManualPromoStatusChange(
                                                  promoKey,
                                                  true
                                                );
                                              }
                                            }}
                                            disabled={
                                              isSelectedPromoRedeemed ||
                                              !isSelectedPromoCancelled ||
                                              isPromoActionLoading ||
                                              (!isSelectedRewardPromo && isSectionDisabled)
                                            }
                                          >
                                            Reactivate
                                          </button>
                                          {!isSelectedRewardPromo && (
                                            <button
                                              type="button"
                                              className="admin-button admin-button--outline admin-button--danger-outline"
                                              onClick={() =>
                                                handleDeleteManualPromo(
                                                  getPromoKey(selectedPromo)
                                                )
                                              }
                                              disabled={isSectionDisabled}
                                            >
                                              Delete
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            className="admin-button admin-button--danger"
                                            onClick={() => {
                                              const promoKey = getPromoKey(selectedPromo);
                                              if (isSelectedRewardPromo) {
                                                updateRewardVoucherStatus(
                                                  selectedPromo.id,
                                                  false
                                                );
                                              } else {
                                                handleManualPromoStatusChange(
                                                  promoKey,
                                                  false
                                                );
                                              }
                                            }}
                                            disabled={
                                              isSelectedPromoRedeemed ||
                                              isSelectedPromoCancelled ||
                                              isPromoActionLoading ||
                                              (!isSelectedRewardPromo && isSectionDisabled)
                                            }
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </>
                                    ) : (
                                      <p className="admin-settings__promo-empty">
                                        Select a promo code to see details.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            </div>
                          </article>

                        </div>
                      )}

                      {settingsActiveSection === "locations" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-locations-settings"
                            className={getSettingsCardClass("locations-settings")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Locations</h3>
                              <p>Set hours, pickup rules, and capacity limits.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <div className="admin-settings__form-grid">
                                <div className="admin-form__row">
                                  <label className="admin-form__label">Location name</label>
                                  <input
                                    className="admin-input"
                                    value={locationsSettings.locationSettings?.name || ""}
                                    onChange={handleValueChange("locations", [
                                      "locationSettings",
                                      "name",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                </div>
                                <div className="admin-form__row">
                                  <label className="admin-form__label">Address</label>
                                  <input
                                    className="admin-input"
                                    value={locationsSettings.locationSettings?.address || ""}
                                    onChange={handleValueChange("locations", [
                                      "locationSettings",
                                      "address",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                </div>
                                <div className="admin-form__row">
                                  <label className="admin-form__label">Operating hours</label>
                                  <input
                                    className="admin-input"
                                    value={locationsSettings.locationSettings?.operatingHours || ""}
                                    onChange={handleValueChange("locations", [
                                      "locationSettings",
                                      "operatingHours",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                </div>
                                <div className="admin-form__row">
                                  <label className="admin-form__label">Pickup instructions</label>
                                  <textarea
                                    className="admin-input admin-input--textarea"
                                    value={locationsSettings.locationSettings?.pickupInstructions || ""}
                                    onChange={handleValueChange("locations", [
                                      "locationSettings",
                                      "pickupInstructions",
                                    ])}
                                    disabled={isSectionDisabled}
                                  />
                                </div>
                              </div>
                              <div className="admin-settings__card-actions">
                                <button type="button" className="admin-button admin-button--outline">
                                  Manage locations
                                </button>
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-partner-profiles"
                            className={getSettingsCardClass("partner-profiles")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Partner profiles</h3>
                              <p>Legal identity and payout info.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Legal name</label>
                                <input
                                  className="admin-input"
                                  value={locationsSettings.partnerProfiles?.legalName || ""}
                                  onChange={handleValueChange("locations", [
                                    "partnerProfiles",
                                    "legalName",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Contact person</label>
                                <input
                                  className="admin-input"
                                  value={locationsSettings.partnerProfiles?.contactPerson || ""}
                                  onChange={handleValueChange("locations", [
                                    "partnerProfiles",
                                    "contactPerson",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">IBAN</label>
                                <input
                                  className="admin-input"
                                  value={locationsSettings.partnerProfiles?.iban || ""}
                                  onChange={handleValueChange("locations", [
                                    "partnerProfiles",
                                    "iban",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Payout schedule</label>
                                <select
                                  className="admin-select"
                                  value={locationsSettings.partnerProfiles?.payoutSchedule || "monthly"}
                                  onChange={handleValueChange("locations", [
                                    "partnerProfiles",
                                    "payoutSchedule",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                </select>
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-commission-model"
                            className={getSettingsCardClass("commission-model")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Commission model</h3>
                              <p>Set how partner payouts are calculated.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Model</label>
                                <select
                                  className="admin-select"
                                  value={locationsSettings.commissionModel?.model || "percentage"}
                                  onChange={handleValueChange("locations", [
                                    "commissionModel",
                                    "model",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="percentage">% of revenue</option>
                                  <option value="fixed">Fixed fee per booking</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Rate</label>
                                <input
                                  className="admin-input"
                                  value={locationsSettings.commissionModel?.rate || ""}
                                  onChange={handleValueChange("locations", [
                                    "commissionModel",
                                    "rate",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-equipment-assignment"
                            className={getSettingsCardClass("equipment-assignment")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Equipment assignment</h3>
                              <p>Allocate stock to partner locations.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Stock model</label>
                                <select
                                  className="admin-select"
                                  value={locationsSettings.equipmentAssignment?.stockModel || "pool"}
                                  onChange={handleValueChange("locations", [
                                    "equipmentAssignment",
                                    "stockModel",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="pool">Shared pool</option>
                                  <option value="fixed">Fixed stock per location</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Assigned units</label>
                                <input
                                  className="admin-input"
                                  value={locationsSettings.equipmentAssignment?.assignedUnits || ""}
                                  onChange={handleValueChange("locations", [
                                    "equipmentAssignment",
                                    "assignedUnits",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-partner-access"
                            className={getSettingsCardClass("partner-access")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Partner access settings</h3>
                              <p>Limit partner visibility and actions.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(locationsSettings.partnerAccess?.canSeeOwnBookings)}
                                  onChange={handleCheckboxChange("locations", [
                                    "partnerAccess",
                                    "canSeeOwnBookings",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Partner can see only their bookings
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(locationsSettings.partnerAccess?.limitedActions)}
                                  onChange={handleCheckboxChange("locations", [
                                    "partnerAccess",
                                    "limitedActions",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Partner actions: confirm pickup/return only
                              </label>
                            </div>
                          </article>

                          <article
                            id="settings-card-partner-readiness"
                            className={getSettingsCardClass("partner-readiness")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Partner readiness checklist</h3>
                              <p>Verify partner onboarding is complete.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__checklist">
                              {(Array.isArray(locationsSettings.partnerReadiness?.items)
                                ? locationsSettings.partnerReadiness.items
                                : []
                              ).map((item) => (
                                <div key={item} className="admin-settings__check-row">
                                  <FiCheckCircle aria-hidden="true" />
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "payments" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-payment-provider"
                            className={getSettingsCardClass("payment-provider")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Payment provider</h3>
                              <p>Use test mode until you are ready to go live.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Provider</label>
                                <select
                                  className="admin-select"
                                  value={paymentsSettings.provider?.provider || "stripe"}
                                  onChange={handleValueChange("payments", [
                                    "provider",
                                    "provider",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="stripe">Stripe</option>
                                  <option value="revolut">Revolut</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Environment</label>
                                <select
                                  className="admin-select"
                                  value={paymentsSettings.provider?.environment || "Test"}
                                  onChange={handleValueChange("payments", [
                                    "provider",
                                    "environment",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="Test">Test</option>
                                  <option value="Live">Live</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">API key</label>
                                <input
                                  className="admin-input"
                                  type="password"
                                  value={paymentsSettings.provider?.apiKeyMasked || ""}
                                  onChange={handleValueChange("payments", [
                                    "provider",
                                    "apiKeyMasked",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Webhook status</label>
                                <input
                                  className="admin-input"
                                  value={paymentsSettings.provider?.webhookStatus || ""}
                                  onChange={handleValueChange("payments", [
                                    "provider",
                                    "webhookStatus",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                            <div className="admin-settings__card-actions">
                              <button
                                type="button"
                                className="admin-button admin-button--outline"
                                onClick={() => handleSettingsTestAction("Payment webhook")}
                              >
                                Send test payment event
                              </button>
                            </div>
                          </article>

                          <article
                            id="settings-card-refund-rules"
                            className={getSettingsCardClass("refund-rules")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Refund rules</h3>
                              <p>Map booking policy to payment actions.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Default refund policy</label>
                                <select
                                  className="admin-select"
                                  value={paymentsSettings.refundRules?.defaultPolicy || "bookingRules"}
                                  onChange={handleValueChange("payments", [
                                    "refundRules",
                                    "defaultPolicy",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="bookingRules">Use booking rules</option>
                                  <option value="manual">Manual approval</option>
                                </select>
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Manual refund roles</label>
                                <select
                                  className="admin-select"
                                  value={paymentsSettings.refundRules?.manualRefundRoles || "owner"}
                                  onChange={handleValueChange("payments", [
                                    "refundRules",
                                    "manualRefundRoles",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="owner">Owner only</option>
                                  <option value="admin">Owner + Admin</option>
                                </select>
                              </div>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(paymentsSettings.refundRules?.reasonRequired)}
                                  onChange={handleCheckboxChange("payments", [
                                    "refundRules",
                                    "reasonRequired",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Refund reason required
                              </label>
                            </div>
                            <div className="admin-settings__card-actions">
                              <button
                                type="button"
                                className="admin-button admin-button--ghost"
                                onClick={() => handleSettingsResetAction("Refund rules")}
                              >
                                Reset to default
                              </button>
                            </div>
                          </article>

                          <article
                            id="settings-card-payouts"
                            className={getSettingsCardClass("payouts")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Payouts</h3>
                              <p>Automate partner commissions and reports.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Schedule</label>
                                <select
                                  className="admin-select"
                                  value={paymentsSettings.payouts?.schedule || "monthly"}
                                  onChange={handleValueChange("payments", [
                                    "payouts",
                                    "schedule",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                </select>
                              </div>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(paymentsSettings.payouts?.autoCalcCommission)}
                                  onChange={handleCheckboxChange("payments", [
                                    "payouts",
                                    "autoCalcCommission",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Auto-calc commission
                              </label>
                            </div>
                            <div className="admin-settings__card-actions">
                              <button type="button" className="admin-button admin-button--outline">
                                Export payout report
                              </button>
                            </div>
                          </article>

                          <article
                            id="settings-card-invoices"
                            className={getSettingsCardClass("invoices")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Invoices & receipts</h3>
                              <p>Customize receipts sent to customers.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Receipt email template</label>
                                <input
                                  className="admin-input"
                                  value={paymentsSettings.invoices?.receiptTemplate || ""}
                                  onChange={handleValueChange("payments", [
                                    "invoices",
                                    "receiptTemplate",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Invoice prefix</label>
                                <input
                                  className="admin-input"
                                  value={paymentsSettings.invoices?.invoicePrefix || ""}
                                  onChange={handleValueChange("payments", [
                                    "invoices",
                                    "invoicePrefix",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(paymentsSettings.invoices?.pdfEnabled)}
                                  onChange={handleCheckboxChange("payments", [
                                    "invoices",
                                    "pdfEnabled",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                PDF generation enabled
                              </label>
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "notifications" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-admin-booking-alerts"
                            className={getSettingsCardClass("admin-booking-alerts")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Admin booking alerts</h3>
                              <p>Send an email whenever a new booking is completed.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(
                                    notificationsSettings.adminAlerts?.bookingCreated
                                  )}
                                  onChange={handleCheckboxChange("notifications", [
                                    "adminAlerts",
                                    "bookingCreated",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Email me when a booking is created
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Alert recipient email</label>
                                <input
                                  className="admin-input"
                                  type="email"
                                  value={
                                    notificationsSettings.adminAlerts?.bookingRecipientEmail || ""
                                  }
                                  onChange={handleValueChange("notifications", [
                                    "adminAlerts",
                                    "bookingRecipientEmail",
                                  ])}
                                  placeholder="owner@example.com"
                                  disabled={isSectionDisabled}
                                />
                                <p className="admin-form__hint">
                                  Leave blank to fall back to the business contact email.
                                </p>
                              </div>
                            </div>
                          </article>
                          <AdminEmailTemplatesPanel
                            showFeedback={showFeedback}
                            refreshAudit={refreshAudit}
                          />
                        </div>
                      )}

                      {settingsActiveSection === "branding" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-brand-assets"
                            className={getSettingsCardClass("brand-assets")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Brand assets</h3>
                              <p>Update logo and colors without redeploying.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Logo</label>
                                <input
                                  className="admin-input"
                                  type="file"
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Primary color</label>
                                <input
                                  className="admin-input"
                                  value={brandingSettings.brandAssets?.primaryColor || ""}
                                  onChange={handleValueChange("branding", [
                                    "brandAssets",
                                    "primaryColor",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Accent color</label>
                                <input
                                  className="admin-input"
                                  value={brandingSettings.brandAssets?.accentColor || ""}
                                  onChange={handleValueChange("branding", [
                                    "brandAssets",
                                    "accentColor",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-seo-tracking"
                            className={getSettingsCardClass("seo-tracking")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>SEO & tracking</h3>
                              <p>Control analytics tags and cookies.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Google Analytics ID</label>
                                <input
                                  className="admin-input"
                                  value={brandingSettings.seoTracking?.googleAnalyticsId || ""}
                                  onChange={handleValueChange("branding", [
                                    "seoTracking",
                                    "googleAnalyticsId",
                                  ])}
                                  placeholder="G-XXXXXX"
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Meta pixel ID</label>
                                <input
                                  className="admin-input"
                                  value={brandingSettings.seoTracking?.metaPixelId || ""}
                                  onChange={handleValueChange("branding", [
                                    "seoTracking",
                                    "metaPixelId",
                                  ])}
                                  placeholder="123456789"
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(
                                    brandingSettings.seoTracking?.cookieBannerEnabled
                                  )}
                                  onChange={handleCheckboxChange("branding", [
                                    "seoTracking",
                                    "cookieBannerEnabled",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Cookie banner enabled
                              </label>
                            </div>
                          </article>

                          <article
                            id="settings-card-public-content"
                            className={getSettingsCardClass("public-content")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Public content</h3>
                              <p>Control the text shown on the public info page.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Info page text - English</label>
                                <textarea
                                  className="admin-input admin-input--textarea"
                                  value={brandingSettings.publicContent?.infoPageText?.en || ""}
                                  onChange={handleValueChange("branding", [
                                    "publicContent",
                                    "infoPageText",
                                    "en",
                                  ])}
                                  placeholder="Text shown on the info page when English is selected."
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Info page text - German</label>
                                <textarea
                                  className="admin-input admin-input--textarea"
                                  value={brandingSettings.publicContent?.infoPageText?.de || ""}
                                  onChange={handleValueChange("branding", [
                                    "publicContent",
                                    "infoPageText",
                                    "de",
                                  ])}
                                  placeholder="Text shown on the info page when German is selected."
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">FAQ</label>
                                <textarea
                                  className="admin-input admin-input--textarea"
                                  value={brandingSettings.publicContent?.faq || ""}
                                  onChange={handleValueChange("branding", [
                                    "publicContent",
                                    "faq",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Terms & privacy links</label>
                                <input
                                  className="admin-input"
                                  value={brandingSettings.publicContent?.termsLink || ""}
                                  onChange={handleValueChange("branding", [
                                    "publicContent",
                                    "termsLink",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Emergency contact</label>
                                <input
                                  className="admin-input"
                                  value={brandingSettings.publicContent?.emergencyContact || ""}
                                  onChange={handleValueChange("branding", [
                                    "publicContent",
                                    "emergencyContact",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "security" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-roles-permissions"
                            className={getSettingsCardClass("roles-permissions")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Roles & permissions</h3>
                              <p>Preset access profiles for owner, staff, and partners.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__matrix">
                              <div className="admin-settings__matrix-row">
                                <strong>Owner</strong>
                                <span>Full access + refunds + settings</span>
                              </div>
                              <div className="admin-settings__matrix-row">
                                <strong>Staff</strong>
                                <span>Bookings + inventory, no payouts</span>
                              </div>
                              <div className="admin-settings__matrix-row">
                                <strong>Partner</strong>
                                <span>Pickup/return only</span>
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-login-security"
                            className={getSettingsCardClass("login-security")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Login security</h3>
                              <p>Harden access to owner-level actions.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(securitySettings.loginSecurity?.requireTwoFactor)}
                                  onChange={handleCheckboxChange("security", [
                                    "loginSecurity",
                                    "requireTwoFactor",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                2FA required for owner
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Session timeout</label>
                                <input
                                  className="admin-input"
                                  value={securitySettings.loginSecurity?.sessionTimeout || ""}
                                  onChange={handleValueChange("security", [
                                    "loginSecurity",
                                    "sessionTimeout",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Allowed domains</label>
                                <input
                                  className="admin-input"
                                  value={securitySettings.loginSecurity?.allowedDomains || ""}
                                  onChange={handleValueChange("security", [
                                    "loginSecurity",
                                    "allowedDomains",
                                  ])}
                                  placeholder="wayli.at"
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-admin-keys"
                            className={getSettingsCardClass("admin-keys")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Admin keys</h3>
                              <p>Rotate admin keys with audit logging.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <p className="admin-settings__warning">
                                Rotating keys will invalidate existing integrations.
                              </p>
                              <button type="button" className="admin-button admin-button--danger">
                                Rotate admin key
                              </button>
                            </div>
                          </article>

                          <article
                            id="settings-card-admin-access-pin"
                            className={getSettingsCardClass("admin-access-pin")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Admin access PIN</h3>
                              <p>Optional fallback PIN for all booking pickup and return actions.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(securitySettings.adminAccessPin?.enabled)}
                                  onChange={handleCheckboxChange("security", [
                                    "adminAccessPin",
                                    "enabled",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Allow a global admin PIN for every booking action
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Admin access PIN</label>
                                <input
                                  className="admin-input"
                                  type="password"
                                  inputMode="numeric"
                                  pattern="[0-9]{4,6}"
                                  title="4 to 6 digits"
                                  maxLength={6}
                                  value={securitySettings.adminAccessPin?.pin || ""}
                                  onChange={handleValueChange("security", [
                                    "adminAccessPin",
                                    "pin",
                                  ])}
                                  placeholder="4 to 6 digits"
                                  disabled={isSectionDisabled}
                                />
                                <p className="admin-form__hint">
                                  When enabled, staff can use either this PIN or the location-specific
                                  partner PIN.
                                </p>
                              </div>
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "integrations" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-calendar-export"
                            className={getSettingsCardClass("calendar-export")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Calendar export</h3>
                              <p>Subscribe to bookings in external calendars.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(integrationsSettings.calendarExport?.enableIcal)}
                                  onChange={handleCheckboxChange("integrations", [
                                    "calendarExport",
                                    "enableIcal",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Enable iCal feeds per location
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label">iCal URL</label>
                                <input
                                  className="admin-input"
                                  value={integrationsSettings.calendarExport?.icalUrl || ""}
                                  onChange={handleValueChange("integrations", [
                                    "calendarExport",
                                    "icalUrl",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-accounting-export"
                            className={getSettingsCardClass("accounting-export")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Accounting export</h3>
                              <p>Configure accountant-friendly exports.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">CSV format</label>
                                <select
                                  className="admin-select"
                                  value={integrationsSettings.accountingExport?.csvFormat || "accountant"}
                                  onChange={handleValueChange("integrations", [
                                    "accountingExport",
                                    "csvFormat",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="accountant">Accountant-friendly</option>
                                  <option value="simple">Simple</option>
                                </select>
                              </div>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(
                                    integrationsSettings.accountingExport?.monthlyPack
                                  )}
                                  onChange={handleCheckboxChange("integrations", [
                                    "accountingExport",
                                    "monthlyPack",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Monthly accountant pack
                              </label>
                            </div>
                          </article>

                          <article
                            id="settings-card-maps"
                            className={getSettingsCardClass("maps")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Maps</h3>
                              <p>Configure map provider credentials.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Google Maps API key</label>
                                <input
                                  className="admin-input"
                                  type="password"
                                  value={integrationsSettings.maps?.googleMapsApiKey || ""}
                                  onChange={handleValueChange("integrations", [
                                    "maps",
                                    "googleMapsApiKey",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-webhooks"
                            className={getSettingsCardClass("webhooks")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Webhooks</h3>
                              <p>Notify downstream systems on booking events.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(integrationsSettings.webhooks?.bookingCreated)}
                                  onChange={handleCheckboxChange("integrations", [
                                    "webhooks",
                                    "bookingCreated",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Booking created
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(integrationsSettings.webhooks?.bookingPaid)}
                                  onChange={handleCheckboxChange("integrations", [
                                    "webhooks",
                                    "bookingPaid",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Booking paid
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(integrationsSettings.webhooks?.bookingCompleted)}
                                  onChange={handleCheckboxChange("integrations", [
                                    "webhooks",
                                    "bookingCompleted",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Booking completed
                              </label>
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(integrationsSettings.webhooks?.bookingRefunded)}
                                  onChange={handleCheckboxChange("integrations", [
                                    "webhooks",
                                    "bookingRefunded",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Booking refunded
                              </label>
                            </div>
                          </article>
                        </div>
                      )}

                      {settingsActiveSection === "audit" && (
                        <div className="admin-settings__section-grid">
                          <article
                            id="settings-card-audit-log"
                            className={getSettingsCardClass("audit-log")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Audit log settings</h3>
                              <p>Control how long critical events are retained.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <label className="admin-settings__checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(auditSettings.auditLog?.logSensitiveEdits)}
                                  onChange={handleCheckboxChange("audit", [
                                    "auditLog",
                                    "logSensitiveEdits",
                                  ])}
                                  disabled={isSectionDisabled}
                                />{" "}
                                Log refunds, pricing, and partner edits
                              </label>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Retention duration</label>
                                <select
                                  className="admin-select"
                                  value={auditSettings.auditLog?.retentionDays || "365"}
                                  onChange={handleValueChange("audit", [
                                    "auditLog",
                                    "retentionDays",
                                  ])}
                                  disabled={isSectionDisabled}
                                >
                                  <option value="90">90 days</option>
                                  <option value="180">180 days</option>
                                  <option value="365">1 year</option>
                                  <option value="730">2 years</option>
                                </select>
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-system-health"
                            className={getSettingsCardClass("system-health")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>System health</h3>
                              <p>Monitor critical services.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__status-list">
                              {systemHealthServices.map((service) => (
                                <div key={service.name}>
                                  <strong>{service.name}</strong>
                                  <span>{service.status}</span>
                                </div>
                              ))}
                            </div>
                          </article>

                          <article
                            id="settings-card-season-dates"
                            className={getSettingsCardClass("season-dates")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Season dates</h3>
                              <p>Limit bookings to the active season window.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__form-grid">
                              <div className="admin-form__row">
                                <label className="admin-form__label">Season start date</label>
                                <input
                                  className="admin-input"
                                  type="date"
                                  value={bookingSettings.window?.seasonStartDate || ""}
                                  onChange={handleValueChange("booking", [
                                    "window",
                                    "seasonStartDate",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                              <div className="admin-form__row">
                                <label className="admin-form__label">Season end date</label>
                                <input
                                  className="admin-input"
                                  type="date"
                                  value={bookingSettings.window?.seasonEndDate || ""}
                                  onChange={handleValueChange("booking", [
                                    "window",
                                    "seasonEndDate",
                                  ])}
                                  disabled={isSectionDisabled}
                                />
                              </div>
                            </div>
                          </article>

                          <article
                            id="settings-card-backups-exports"
                            className={getSettingsCardClass("backups-exports")}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Backups & exports</h3>
                              <p>Export full datasets for compliance.</p>
                            </div>
                            <div className="admin-settings__card-body admin-settings__button-grid">
                              <button
                                type="button"
                                className="admin-button admin-button--outline"
                                onClick={handleExportBookings}
                                disabled={isSectionDisabled}
                              >
                                Export all bookings
                              </button>
                              <button
                                type="button"
                                className="admin-button admin-button--outline"
                                onClick={handleExportPayments}
                                disabled={isSectionDisabled}
                              >
                                Export payments & refunds
                              </button>
                              <button
                                type="button"
                                className="admin-button admin-button--outline"
                                onClick={handleExportPartners}
                                disabled={isSectionDisabled}
                              >
                                Export partners & payouts
                              </button>
                            </div>
                          </article>

                          <article
                            id="settings-card-danger-zone"
                            className={`${getSettingsCardClass("danger-zone")} admin-settings__card--danger`.trim()}
                          >
                            <div className="admin-settings__card-head">
                              <h3>Danger zone</h3>
                              <p>Destructive actions require confirmation.</p>
                            </div>
                            <div className="admin-settings__card-body">
                              <p className="admin-settings__warning">
                                Type <strong>CONFIRM</strong> to proceed.
                              </p>
                              <div className="admin-settings__form-grid">
                                <div className="admin-form__row">
                                  <label className="admin-form__label">Type CONFIRM</label>
                                  <input
                                    className="admin-input"
                                    placeholder="CONFIRM"
                                    value={dangerConfirmText}
                                    onChange={(event) => setDangerConfirmText(event.target.value)}
                                    disabled={isSectionDisabled}
                                  />
                                </div>
                              </div>
                              <div className="admin-settings__danger-actions">
                                <button
                                  type="button"
                                  className="admin-button admin-button--danger"
                                  onClick={handleBookingGateToggle}
                                  disabled={isSectionDisabled || !isDangerConfirmed}
                                >
                                  <FiAlertOctagon aria-hidden="true" />
                                  {bookingsEnabled && paymentsEnabled
                                    ? " Disable bookings and payments"
                                    : " Enable bookings and payments"}
                                </button>
                              </div>
                            </div>
                          </article>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </div>
  );
};

export default AdminSettingsTab;
